import { memo, useEffect, useRef } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';

import { Box, Grid, Heading } from '@simplywallst/ui-core';

import If from '@/components/If';
import { OneColumn } from '@/components/Layout';

import type { Entity } from '../../../redux/selectors/myMarketsAndIndustriesSelectors';

import useFeed from '../../../hooks/useFeed';
import useFeedSorting from '../../../hooks/useFeedSorting';
import useImpressionTracking from '../../../hooks/useImpressionTracking';

import Empty from '../../Empty';
import InternationalMarkets from '../../InternationalMarkets';
import MarketTopWorst from '../../MarketTopWorst';
import { MyRecentNotes } from '../../MyRecentNotes';
import { PortfoliosLoadingState } from '../../Portfolios/components/PortfoliosLoadingState';
import RecentlyViewed from '../../RecentlyViewed';
import { SortingDropdownButton } from '../../SortingDropdownButton';
import UpcomingEvents from '../../UpcomingEvents';

import FeedCard, { FeedCardSkeleton } from '@/components/FeedCard';
import RecentArticles from '@/components/RecentArticles';

const DeprecatedPortfolios = loadable(
  () => import('../../DeprecatedPortfolios')
);
const PortfoliosSection = loadable(() => import('../../Portfolios'), {
  resolveComponent: (c) => c.PortfoliosSection,
  fallback: <PortfoliosLoadingState />,
});
const DeprecatedMyMarkets = loadable(() => import('../../DeprecatedMyMarkets'));
const Follow = loadable(() => import('../../Follow'));
const Footer = loadable(() => import('@/components/Footer'));
const BrazeContentCard = loadable(
  () => import('@/components/BrazeContentCard')
);

const MemoMarketTopWorst = memo(MarketTopWorst); // Until the Dashboard MVP is iterated on

export interface Props {
  isUserLoggedIn: boolean;
  isOnboardingSlideShowDismissed: boolean;
  myMarketKeys: string[];
  myIndustryKeys: string[];
  myMarketAndIndustryEntities: Record<string, Entity>;
  myMarketsAndIndustriesLastUpdated: number | null;
}

export const DashboardDesktop = ({
  isUserLoggedIn,
  isOnboardingSlideShowDismissed,
  myMarketKeys,
  myIndustryKeys,
  myMarketAndIndustryEntities,
  myMarketsAndIndustriesLastUpdated,
}: Props) => {
  const { keys: feedKeys, entities, isFetched, isFetching } = useFeed();
  const { options, handleSelect, handleSorting, selectedSortType } =
    useFeedSorting(entities);

  const trackFeedFetch = useRef(false);

  const { onIntersection } = useImpressionTracking();

  const { trackEvent } = useTracking();

  useEffect(() => {
    if (isFetched && !trackFeedFetch.current) {
      const feedItemTypeCount = feedKeys.reduce((a, b) => {
        entities[b].feedItems.forEach(({ type }) => {
          const key = `${type}Count`;
          a[key] = a[key] ? a[key] + 1 : 1;
        });
        return a;
      }, {});
      trackEvent({
        type: 'track',
        action: 'feed_fetch_complete',
        feedLength: feedKeys.length,
        ...feedItemTypeCount,
      });
      trackFeedFetch.current = true;
    }
  }, [isFetched, trackFeedFetch, feedKeys, entities, trackEvent]);

  const isFeedEmpty = isFetched && feedKeys.length === 0;

  return (
    <OneColumn>
      <Box as="section" marginTopLg="x2" marginBottomLg="x2">
        <Grid
          templateColumns="auto"
          templateColumnsLg="auto 25%"
          gapMd="30px"
          gap="12px"
        >
          {isOnboardingSlideShowDismissed ? (
            <Grid templateColumns="1fr 2fr" gap="30px">
              <Grid alignContent="start">
                <DeprecatedPortfolios />
                <Box as="section" marginBottom="x4">
                  <Box marginTop="x1" marginBottom="x2">
                    <Heading
                      as="h1"
                      typography="xSmall"
                      color="whiteRGBA"
                      opacity={0.7}
                    >
                      My Markets &amp; Industries
                    </Heading>
                  </Box>
                  <DeprecatedMyMarkets
                    marketKeys={myMarketKeys}
                    industryKeys={myIndustryKeys}
                    lastUpdated={myMarketsAndIndustriesLastUpdated}
                    entities={myMarketAndIndustryEntities}
                  />
                </Box>
                <MyRecentNotes />
                <UpcomingEvents />
                <Box as="section" marginBottom="x4">
                  <Box marginTop="x1" marginBottom="x2">
                    <Heading
                      as="h1"
                      typography="xSmall"
                      color="whiteRGBA"
                      opacity={0.7}
                    >
                      Recently Viewed
                    </Heading>
                  </Box>
                  <RecentlyViewed />
                </Box>
              </Grid>
              <Grid>
                <Box
                  position="relative"
                  zIndex={1}
                  marginTop="x1"
                  marginBottom="x1"
                  marginTopLg="x0"
                  marginBottomLg="x0"
                >
                  <Grid
                    templateColumns="max-content auto"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box marginTop="x1" marginBottom="x2">
                      <Heading
                        as="h1"
                        typography="xSmall"
                        color="whiteRGBA"
                        opacity={0.7}
                      >
                        My Updates
                      </Heading>
                    </Box>
                    {!isFeedEmpty && (
                      <Box
                        paddingTop="x1"
                        paddingBottom="x1"
                        paddingBottomLg="x2"
                        paddingRight="x2"
                        paddingLeft="x2"
                        paddingRightLg="x0"
                      >
                        <SortingDropdownButton
                          options={options}
                          handleSelect={handleSelect}
                          selectedSortType={selectedSortType}
                          disabled={!isFetched}
                        />
                      </Box>
                    )}
                  </Grid>
                </Box>
                {isUserLoggedIn && (
                  <BrazeContentCard
                    feedType="dashboard"
                    anchorTarget="_blank"
                  />
                )}
                {isFetching ? (
                  <>
                    <FeedCardSkeleton />
                    <FeedCardSkeleton />
                    <FeedCardSkeleton />
                  </>
                ) : isFeedEmpty ? (
                  <Empty />
                ) : (
                  feedKeys
                    .sort(handleSorting)
                    .map((n, i) => (
                      <FeedCard
                        key={`feed-card-${n}`}
                        index={i}
                        company={entities[n]}
                        onIntersection={onIntersection}
                        data-cy-id="dashboard-feed-card"
                      />
                    ))
                )}
                <If match={feedKeys.length < 5}>{() => <Follow />}</If>
                {RUNTIME_ENV !== 'native' && <Footer center={true} />}
              </Grid>
            </Grid>
          ) : (
            <PortfoliosSection />
          )}
          <Grid
            column="2"
            templateColumns="auto"
            alignContent="start"
            gap="16px"
          >
            <RecentArticles />
            {isOnboardingSlideShowDismissed ? (
              <Box padding="x2" paddingLg="x0">
                <Box as="section" marginBottom="x4">
                  <Box marginTop="x1" marginBottom="x2">
                    <Heading
                      as="h1"
                      typography="xSmall"
                      color="whiteRGBA"
                      opacity={0.7}
                    >
                      Market Performance
                    </Heading>
                  </Box>
                  <MemoMarketTopWorst />
                </Box>
                <InternationalMarkets />
              </Box>
            ) : null}
          </Grid>
        </Grid>
      </Box>

      <Grid templateColumns="1fr auto 25%" gap="30px">
        {!isOnboardingSlideShowDismissed && (
          <Grid alignContent="start">
            <DeprecatedPortfolios />
            <Box as="section" marginBottom="x4">
              <Box marginTop="x1" marginBottom="x2">
                <Heading
                  as="h1"
                  typography="xSmall"
                  color="whiteRGBA"
                  opacity={0.7}
                >
                  My Markets &amp; Industries
                </Heading>
              </Box>
              <DeprecatedMyMarkets
                marketKeys={myMarketKeys}
                industryKeys={myIndustryKeys}
                lastUpdated={myMarketsAndIndustriesLastUpdated}
                entities={myMarketAndIndustryEntities}
              />
            </Box>
            <MyRecentNotes />
            <UpcomingEvents />
            <Box as="section" marginBottom="x4">
              <Box marginTop="x1" marginBottom="x2">
                <Heading
                  as="h1"
                  typography="xSmall"
                  color="whiteRGBA"
                  opacity={0.7}
                >
                  Recently Viewed
                </Heading>
              </Box>

              <RecentlyViewed />
            </Box>
          </Grid>
        )}
        {!isOnboardingSlideShowDismissed && (
          <>
            <Grid>
              <Box
                position="relative"
                zIndex={1}
                marginTop="x1"
                marginBottom="x1"
                marginTopLg="x0"
                marginBottomLg="x0"
              >
                <Grid
                  templateColumns="max-content auto"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box marginTop="x1" marginBottom="x2">
                    <Heading
                      as="h1"
                      typography="xSmall"
                      color="whiteRGBA"
                      opacity={0.7}
                    >
                      My Updates
                    </Heading>
                  </Box>
                  {!isFeedEmpty && (
                    <Box
                      paddingTop="x1"
                      paddingBottom="x1"
                      paddingBottomLg="x2"
                      paddingRight="x2"
                      paddingLeft="x2"
                      paddingRightLg="x0"
                    >
                      <SortingDropdownButton
                        options={options}
                        handleSelect={handleSelect}
                        selectedSortType={selectedSortType}
                        disabled={!isFetched}
                      />
                    </Box>
                  )}
                </Grid>
              </Box>
              {isUserLoggedIn && (
                <BrazeContentCard feedType="dashboard" anchorTarget="_blank" />
              )}
              {isFetching ? (
                <>
                  <FeedCardSkeleton />
                  <FeedCardSkeleton />
                  <FeedCardSkeleton />
                </>
              ) : isFeedEmpty ? (
                <Empty />
              ) : (
                feedKeys
                  .sort(handleSorting)
                  .map((n, i) => (
                    <FeedCard
                      key={`feed-card-${n}`}
                      index={i}
                      company={entities[n]}
                      onIntersection={onIntersection}
                      data-cy-id="dashboard-feed-card"
                    />
                  ))
              )}
              <If match={feedKeys.length < 5}>{() => <Follow />}</If>
              {RUNTIME_ENV !== 'native' && <Footer center={true} />}
            </Grid>
            <Grid alignContent="start">
              <Box padding="x2" paddingLg="x0">
                <Box as="section" marginBottom="x4">
                  <Box marginTop="x1" marginBottom="x2">
                    <Heading
                      as="h1"
                      typography="xSmall"
                      color="whiteRGBA"
                      opacity={0.7}
                    >
                      Market Performance
                    </Heading>
                  </Box>
                  <MemoMarketTopWorst />
                </Box>
                <InternationalMarkets />
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </OneColumn>
  );
};
